import ApplicationCulture from '../config/ApplicationCulture.js';
import TypeHelper from './TypeHelper.js';
import NumberHelper from './NumberHelper.js';
import StringHelper from './StringHelper.js';

const DateHelper = (function() {

    // Ticks
    const _sec = 1000;
    const _minute = 60000;
    const _hour = 3600000;
    const _day = 86400000;

    const _dateTestPatterns = Object.freeze({
        BR: /\d{1,2}[\/\.]\d{1,2}[\/\.]\d{1,4}/,
        ISO_D: /\d{4}-\d{2}-\d{2}/,
        ISO_DT: /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z?/i,
    });
    
    /**
     * Gera um {@link Date} com a data mínima padrão da aplicação.
     * @public
     * @function
     * @returns {Date}
     */
    const DefaultMinimumDate = () => {
        return new Date(1970,0,1);
    };
    
    /**
     * Gera um {@link Date} com a data máxima padrão da aplicação. A data máxima será
     * sempre em 31 de dezembro do ano atual mais 99.
     * @public
     * @function
     * @returns {Date}
     */
    const MaximumDate = () => {
        let ref = new Date();
        let year = ref.getFullYear() + 99;
        let month = 11;
        let day = 31;
        let max = new Date(year, month, day);
        return max;
    };
    
    /**
     * A maior data e hora aceita pela aplicação.
     * @public
     * @function
     * @returns {Date} A maior data e hora aceita pela aplicação.
     */
    const MaximumDateTime = () => {
        let ref = new Date();
        let year = ref.getFullYear() + 100;
        let month = ref.getMonth();
        let day = ref.getDate();
        let max = new Date(year, month, day, 23, 59, 59);
        
        if ( month === 1 && max.getMonth() !== month ) {
            day = 28;
            return new Date(year, month, day, 23, 59, 59);
        }
    
        return max;
    };
    
    /**
     * A menor data. Dia 1º de janeiro do ano 1, com hora zerada.
     * @public
     * @function
     * @returns {Date} A data do dia 1 de janeiro do ano 1, com hora zerada.
     */
    const MinimumDateTime = () => {
        return new Date("0001-01-01T00:00:00");
    };
    
    /**
     * A menor data com valor positivo. Dia 1º de janeiro do ano 1900, com hora zerada.
     * @public
     * @function
     * @returns {Date} A data do dia 1º de janeiro do ano 1900, com hora zerada.
     */
    const MinimumPositiveDateTime = () => {
        return new Date(0,0,1);
    };
    
    const _jsDateTicks = MinimumPositiveDateTime().getTime();
    
    /**
     * Verifica se o dado informado é um Date com valor diferente de NaN.
     * @public
     * @function
     * @param {Date} value - Objeto que será analisado.
     * @returns {boolean} Se o valor informado é um Date e com valor diferente de NaN.
     */
    const IsNullOrEmpty = (value) => {
    
        return (
            !TypeHelper.IsDate(value) 
            || isNaN(value.getTime())
        );
    };
    
    /**
     * Verifica se a data é válida.
     * @public
     * @function
     * @param {Date} date Data a ser validada.
     * @returns {boolean} Se a data é válida ou não.
     */
    const IsValid = (date) => {
        
        if ( !TypeHelper.IsDate(date) || isNaN(date.getTime()) ) return false;
    
        var dateTicks = date.getTime();
    
        return dateTicks > _jsDateTicks;
    };
    
    /**
     * Converte uma data no formato string em um {@link Date}. Os formatos de da data e 
     * hora aceitos estão catalogados na variável {@link _dateTestPatterns}.
     * @public
     * @function
     * @param {string} data Data ou data e hora no formato string.
     * @param {null | Date} [onFail = null] Dado que será retornado em caso de falha.
     * @returns {null | Date}
     */
    const ToDate = (data, onFail = null) => {
    
        // É um Date válido?
        if ( 
            TypeHelper.IsDate(data) 
            && !isNaN(data.getTime()) 
        ) return data;
    
        // Se onFail não for nulo e não for um Date válido, então onFail será nulo.
        if ( 
            !TypeHelper.IsNull(onFail) 
            && !IsValid(onFail)
        ) onFail = null;
    
        //  Se dado for uma null, string vazia ou um Date inválido, então retorna onFail.
        if ( 
            StringHelper.IsNullOrAnySpaces(data)
            || (
                TypeHelper.IsDate(data) 
                && isNaN(data.getTime()) 
            ) 
        ) return onFail;
    
        if ( _dateTestPatterns.BR.test(data) ) {
    
            //  Esse RegEx pega datas no padrão BR.
    
            let matches = [...data.matchAll(/\d+/g)];
    
            //  DD/MM/YYYY
            let year = matches[2][0].padStart(4, "0");
            let month = matches[1][0].padStart(2, "0");
            let day = matches[0][0].padStart(2, "0");
    
            return new Date(`${year}-${month}-${day}T00:00:00`);
    
        } else if ( _dateTestPatterns.ISO_DT.test(data) ) {
    
            var txtDate = data.match(_dateTestPatterns.ISO_DT)[0];
    
            var resultDate = new Date(txtDate);
    
            return resultDate;
    
        } else if ( _dateTestPatterns.ISO_D.test(data) ) {
    
            let matches = [...data.matchAll(/\d+/g)];
            let year = matches[0][0].padStart(4, "0");
            let month = matches[1][0].padStart(2, "0");
            let day = matches[2][0].padStart(2, "0");
    
            return new Date(`${year}-${month}-${day}T00:00:00`);
        }
    
        return onFail;
    };
    
    /**
     * Converte a data informado em string no formato da cultura atual da aplicação.
     * "Invalid Date" serão convertidos em "01/01/0001".
     * @public
     * @function
     * @param {Date} date A data informada que será convertida em string.
     * @param {boolean} [nullOnFail = true] Ao ocorrer uma falha deve ser retornado null?
     * @param {boolean} [acceptInvalidDate = false] Aceitar datas inválidas (inferiores a 02/01/1900)?
     * @returns {string} A data informada no formatua da cultura atual da aplicação.
    */
    const ToDateString = (
        date,
        nullOnFail = true,
        acceptInvalidDate = false,
    ) => {
    
        if ( !TypeHelper.IsBoolean(nullOnFail) ) nullOnFail = true;
        if ( !TypeHelper.IsBoolean(acceptInvalidDate) ) acceptInvalidDate = false;
    
        if ( TypeHelper.IsString(date) ) date = ToDate(date);
    
        if ( 
            (
                //  Não aceita datas inválidas e a data é inválida.
                !acceptInvalidDate
                && !IsValid(date) 
            )
            ||
            (
                //  Aceita datas inválidas mas a data informada é um "Invalid Date", new Date(undefined) 
                //  por exemplo, ou não é do tipo DATE.
                acceptInvalidDate
                && (
                    !TypeHelper.IsDate(date) 
                    || isNaN(date.getDate())
                )
            )
        )
        {
            return nullOnFail ? null : ToDateString(MinimumDateTime(), false, true);
        }
    
        return date.toLocaleDateString("pt-BR");
    };

    /**
     * Converte a data informada em string no formato "MM/YYYY".
     * "Invalid Date" serão convertidos em "".
     * @public
     * @function
     * @param {Date} date A data informada que será convertida em string.
     * @param {boolean} [acceptInvalidDate = false] Aceitar datas inválidas (inferiores a 02/01/1900)?
     * @returns {string} A data informada no formato "MM/YYYY".
     */
    const ToMonthYearString = (
        date,
        acceptInvalidDate = false,
    ) => {

        if ( !TypeHelper.IsBoolean(acceptInvalidDate) ) acceptInvalidDate = false;

        if ( TypeHelper.IsString(date) ) date = ToDate(date);

        if ( 
            (
                //  Não aceita datas inválidas e a data é inválida.
                !acceptInvalidDate
                && !IsValid(date) 
            )
            ||
            (
                //  Aceita datas inválidas mas a data informada é um "Invalid Date", new Date(undefined) 
                //  por exemplo, ou não é do tipo DATE.
                acceptInvalidDate
                && (
                    !TypeHelper.IsDate(date) 
                    || isNaN(date.getDate())
                )
            )
        )
        {
            return "";
        }

        return date.toLocaleDateString(
            ApplicationCulture.CurrentCulture.Name,
            {
                month: "2-digit",
                year: "numeric",
            }
        );
    };
    
    /**
     * Retorna o próximo dia útil, ignorando se é ou não feriado.
     * @public
     * @function
     * @param {Date} [referenceDate = null] Data de referência para determinar o próximo dia útil. Deve ser igual ou maior que a data do dia.
     * @returns {Date} O próximo dia útil.
     */
    const NextBusinessDay = (referenceDate = null) => {
    
        let now = new Date();
        let today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); 
    
        if ( 
            !IsValid(referenceDate) 
            || referenceDate.getTime() < today.getTime()
        ) {
    
            referenceDate = today;
        }
    
        let weekDay = referenceDate.getDay();
        let factor = (
            weekDay >= 0 && weekDay < 5
                ? 1
                : weekDay === 5 ? 3 : 2
        );
        const nextBusinessDay = new Date(
            referenceDate.getFullYear(),
            referenceDate.getMonth(),
            referenceDate.getDate() + factor
        );
        return nextBusinessDay;
    };
    
    /**
     * Aplica a máscara 'dd/MM/yyyy' a uma string.
     * @private
     * @function
     * @param {string} dataText O texto ao qual será aplicado a máscara.
     * @returns {string} O texto adaptado a máscara de data da cultura pt-BR.
     */
    const _applyDateMask_ptBR = (dataText) => {
    
        if ( StringHelper.IsNullOrAnySpaces(dataText) ) return "";
    
        dataText = StringHelper.RemoveAllNonDigits(dataText);

        switch (dataText.length) {
            case 3:
            case 4:
                dataText = dataText.substring(0, 2) + "/" + dataText.substring(2, 4);
                break;

            case 5:
            case 6:
            case 7:
            case 8:
                dataText = dataText.substring(0, 2) + "/" + dataText.substring(2, 4) + "/" + dataText.substring(4, 8);
                break;
        }

        return dataText;
    };

    /**
     * Aplica a máscara de data da cultura atual da aplicação.
     * @public
     * @function
     * @param {string} dataText - Texto a ser formatado.
     * @returns {string} - Texto formatado.
     */
    const ApplyDateMask = (dataText) => {

        switch ( ApplicationCulture.CurrentCulture.LCID ) {

            case ApplicationCulture.Brazil.LCID: return _applyDateMask_ptBR(dataText);

            default: return dataText;
        }
    };

    const _defaultMinimumYear = DefaultMinimumDate().getFullYear();
    const _jsMinimumYear = MinimumPositiveDateTime().getFullYear();
    const _maxDaysInterval = 21900; // 60 anos
    
    const _helper = {
        DefaultMinimumDate,
        MaximumDate,
        MaximumDateTime,
        IsNullOrEmpty,
        IsValid,
        ToDate,
        ToDateString,
        ToMonthYearString,
        NextBusinessDay,
        ApplyDateMask,
    };

    Object.defineProperty(_helper, "UnixMinimumYear", {
        get: () => _defaultMinimumYear,
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "JsMinimumYear", {
        get: () => _jsMinimumYear,
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "MaxDaysInterval", {
        get: () => _maxDaysInterval,
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "Now", {
        get: () => new Date(),
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "Today", {
        get: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth(), now.getDate());
        },
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "Tomorrow", {
        get: () => {
            var now = _helper.Today.getTime();
            return new Date(now + _day);
        },
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "Yesterday", {
        get: () => {
            var now = _helper.Today.getTime();
            return new Date(now - _day);
        },
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "DefaultMinimumDate", {
        get: () => DefaultMinimumDate(),
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "MaximumDate", {
        get: () => MaximumDate(),
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "MaximumDateTime", {
        get: () => MaximumDateTime(),
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "MinimumDateTime", {
        get: () => MinimumPositiveDateTime(),
        set: () => {throw TypeError("Assignment to constant property.");}
    });

    //  START - DotNet - Funções específicas
    const _dotNetFunctions = {};
    Object.defineProperty(_dotNetFunctions, "DefaultDate", {
        get: MinimumDateTime,
        set: () => { throw TypeError("Assignment to constant property."); }
    });
    Object.freeze(_dotNetFunctions);
    Object.defineProperty(_helper, "DotNet", {
        get: () => _dotNetFunctions,
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    //  END - DotNet - Funções específicas

    //  START - Javascript - Funções específicas
    const _jsFunctions = {};
    Object.defineProperty(_jsFunctions, "DefaultDate", {
        get: MinimumPositiveDateTime,
        set: () => { throw TypeError("Assignment to constant property."); }
    });
    Object.freeze(_jsFunctions);
    Object.defineProperty(_helper, "JS", {
        get: () => _jsFunctions,
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    //  END - Javascript - Funções específicas

    return Object.freeze(_helper);

})();

export default DateHelper;